import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { useTooltipAutoClose, useTooltipPortalFade } from "../../../lib/useTooltipAutoClose";
import classes from "./BodySimpleModal.module.css";

let bodySimpleModalV3IdCounter = 0;
const TOOLTIP_OPEN_EVENT = "workq-deferred-tooltip-open";

export default function BodySimpleModalV3({
    children,
    message,
    isFlex,
    disabled,
    widthAuto,
    fixedWidth,
    bottomDropDirection,
    /** When nested in `WorkQDeferredTooltip`, must match parent `tooltipIdRef` or the parent clears hover on every open. */
    tooltipBroadcastId,
    openOnMount,
    /** Full cell hit area in work-queue table cells only — do not use in header/toolbars. */
    fillTrigger,
}) {
    const messageRef = useRef(null);
    const tooltipElRef = useRef(null);
    const broadcastIdRef = useRef(null);
    const { isOpen, isVisible, isClosing, open, close } = useTooltipPortalFade();
    const [coordinates, setCoordinates] = useState({
        top: null,
        left: null,
    });

    if (broadcastIdRef.current == null) {
        if (tooltipBroadcastId != null) {
            broadcastIdRef.current = tooltipBroadcastId;
        } else {
            bodySimpleModalV3IdCounter += 1;
            broadcastIdRef.current = bodySimpleModalV3IdCounter;
        }
    }

    const updateCoordinates = useCallback(() => {
        if (messageRef.current) {
            const rect = messageRef.current.getBoundingClientRect();
            setCoordinates({
                top: rect.top,
                left: rect.left + rect.width / 2,
            });
        }
    }, []);

    useLayoutEffect(() => {
        if (isOpen) {
            updateCoordinates();
        }
    }, [isOpen, updateCoordinates]);

    useTooltipAutoClose({
        isOpen,
        onClose: close,
        triggerRef: messageRef,
        tooltipRef: tooltipElRef,
        interactive: true,
    });

    const openTooltipFromHover = useCallback(() => {
        window.dispatchEvent(
            new CustomEvent(TOOLTIP_OPEN_EVENT, {
                detail: { tooltipId: broadcastIdRef.current },
            }),
        );
        open();
        updateCoordinates();
    }, [open, updateCoordinates]);

    useLayoutEffect(() => {
        if (disabled || !message) {
            return;
        }
        if (openOnMount || messageRef.current?.matches(":hover")) {
            openTooltipFromHover();
        }
    }, [disabled, message, openOnMount, openTooltipFromHover]);

    useEffect(() => {
        const onTooltipOpened = (event) => {
            if (event?.detail?.tooltipId === broadcastIdRef.current) {
                return;
            }
            close();
        };
        window.addEventListener(TOOLTIP_OPEN_EVENT, onTooltipOpened);
        return () => window.removeEventListener(TOOLTIP_OPEN_EVENT, onTooltipOpened);
    }, [close]);

    function pointerEnterHandler() {
        if (!disabled) {
            openTooltipFromHover();
        }
    }

    const overlayRoot = document.getElementById("overlay-root");

    return (
        <div
            className={`${fillTrigger ? classes.tooltip_trigger : ""} ${isFlex ? classes.flex_container : ""}`}
            ref={messageRef}
            onPointerEnter={pointerEnterHandler}
        >
            {children}
            {isVisible &&
                message &&
                overlayRoot &&
                ReactDOM.createPortal(
                    <div
                        className={`${classes.fixed_message} ${widthAuto ? classes.width_auto : ""} ${fixedWidth ? classes.fixed_width : ""} ${bottomDropDirection ? classes.bottom_drop_direction : ""} ${isClosing ? classes.closing : ""}`}
                        style={{
                            top: coordinates.top,
                            left: coordinates.left,
                            width: fixedWidth ? fixedWidth : "",
                        }}
                        onPointerEnter={pointerEnterHandler}
                        ref={tooltipElRef}
                        data-tooltip-broadcast-id={broadcastIdRef.current}
                    >
                        {message}
                    </div>,
                    overlayRoot,
                )}
        </div>
    );
}
